import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Download, CreditCard, Building2, Sparkles } from "lucide-react";

import { PageHead, Panel, StatCard } from "@/project/components/dashboard-shell";
import { formatINR } from "@/project/data/sample";
import { usePayouts } from "@/project/lib/queries";
import { downloadCsv } from "@/project/lib/receipt";

export const Route = createFileRoute("/admin/payouts")({ component: AdminPayouts });

function AdminPayouts() {
  const { data: payoutList = [], isLoading } = usePayouts();
  const [q, setQ] = useState("");

  const totalGross = payoutList.reduce((s, p: any) => s + (p.gross_amount || p.gross || 0), 0);
  const totalNet = payoutList.reduce((s, p: any) => s + (p.net_payout || p.net || 0), 0);
  const pendingCount = payoutList.filter((p: any) => p.status !== "Transferred" && p.status !== "Paid").length;

  return (
    <>
      <PageHead
        title="Settlements & Bank Payouts"
        subtitle="Financial ledger, platform fee audits, and NEFT/RTGS bank transfers to verified gaushalas."
        action={
          <button
            onClick={() =>
              downloadCsv("payout-settlements", [
                ["Payout ID", "Organisation", "Gross Amount", "Platform Fee", "Net Payable", "Bank Details", "UTR Reference", "Status", "Date"],
                ...payoutList.map((p: any) => [
                  p.id,
                  p.org_name ?? p.orgId ?? "",
                  p.gross_amount ?? p.gross ?? 0,
                  p.platform_fee ?? p.fee ?? 0,
                  p.net_payout ?? p.net ?? 0,
                  p.bank_account ?? "",
                  p.utr_no ?? p.utr ?? "",
                  p.status,
                  new Date(p.created_at ?? Date.now()).toLocaleDateString("en-IN"),
                ]),
              ])
            }
            className="flex items-center gap-1.5 rounded-full border border-border bg-background px-4 py-2 text-xs font-semibold hover:bg-muted transition"
          >
            <Download className="h-3.5 w-3.5" /> Export Settlement CSV
          </button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Raised" value={formatINR(totalGross)} tone="primary" />
        <StatCard label="Net Settled" value={formatINR(totalNet)} tone="good" />
        <StatCard label="Pending Audits" value={String(pendingCount)} tone="warn" />
        <StatCard label="Platform Fee" value="0%" hint="100% 80G pass-through to Gaushalas" />
      </div>

      {/* AI Settlement & Audit Banner */}
      <div className="mt-6 rounded-2xl border border-amber-200/80 bg-card p-5 shadow-xs">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--maroon)] text-white shadow-xs">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold text-sm text-[var(--maroon-deep)]">AI Payout Release & Settlement Auditor</p>
              <p className="text-xs text-muted-foreground">Automated NEFT/RTGS audit · Bank penny drop verified · 0% platform fee enforcement</p>
            </div>
          </div>
          <button
            onClick={() => toast.success("AI Payout Audit Complete: All pending settlements verified against bank account penny-drop response.")}
            className="flex items-center gap-1.5 rounded-xl bg-[var(--maroon)] px-4 py-2 text-xs font-bold text-white hover:bg-[var(--maroon-deep)] transition shadow-xs"
          >
            <ShieldCheck className="h-4 w-4" /> Run AI Settlement Audit
          </button>
        </div>
      </div>

      <div className="mt-6">
        <Panel title="Gaushala Bank Payout Register">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-sm">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <tr className="text-left">
                  <th className="py-3 px-2">Payout ID</th>
                  <th>Gaushala / Institution</th>
                  <th>Gross Raised</th>
                  <th>Fee (0%)</th>
                  <th>Net Payable</th>
                  <th>Bank Account</th>
                  <th>UTR Reference</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/80 text-xs">
                {payoutList.map((p: any) => {
                  const isPaid = p.status === "Transferred" || p.status === "Paid";
                  return (
                    <tr key={p.id} className="hover:bg-muted/30">
                      <td className="py-3 px-2 font-mono font-bold text-[var(--maroon-deep)]">{p.id}</td>
                      <td className="font-semibold text-foreground">{p.org_name ?? p.orgId}</td>
                      <td className="font-medium">{formatINR(p.gross_amount ?? p.gross ?? 0)}</td>
                      <td className="text-muted-foreground">₹0</td>
                      <td className="font-extrabold text-[var(--maroon)]">{formatINR(p.net_payout ?? p.net ?? 0)}</td>
                      <td className="text-muted-foreground">{p.bank_account ?? "State Bank of India · ****4812"}</td>
                      <td className="font-mono text-[11px] text-foreground/80">{p.utr_no ?? p.utr ?? "—"}</td>
                      <td>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
                            isPaid
                              ? "bg-[var(--success)]/15 text-[var(--success)]"
                              : "bg-[var(--saffron)]/20 text-[var(--saffron-deep)]"
                          }`}
                        >
                          {isPaid ? <CheckCircle2 className="h-3 w-3" /> : <CreditCard className="h-3 w-3" />}
                          {p.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </>
  );
}
